import {
  selectHiddenExportModeTabs,
  selectSelectedExportModeTab,
} from "@/store/modes/export-mode-selectors";
import { setSelectedExportModeTab } from "@/store/modes/export-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { FaroText, grey } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { isValid } from "@faro-lotv/ielement-types";
import { TabContext, TabPanel } from "@mui/lab";
import { Drawer, SxProps, Tab, Tabs } from "@mui/material";
import { useLayoutEffect, useState, type JSX } from "react";
import { useExportModePermissions } from "../overview-mode/use-can-use-export-mode";
import { ExportModeTab } from "./export-mode-types";
import { OrthophotoExportForm } from "./orthophoto-export/orthophoto-export-form";
import { PointCloudExportForm } from "./point-cloud-export/point-cloud-export-form";
import { usePointCloudFormats } from "./point-cloud-export/use-point-cloud-formats";
import { ScansExportForm } from "./scans-export/scans-export-form";
import { useIsExportableDataset } from "./scans-export/use-prepare-scans-export-data";

/** Common styles for the tab panels */
const TAB_PANEL_SX: SxProps = {
  p: 0,
  height: "100%",
};

/** @returns the drawer for the export mode */
export function ExportModeDrawer(): JSX.Element {
  const dispatch = useAppDispatch();

  const hiddenTabs = useAppSelector(selectHiddenExportModeTabs);

  const selectedTab = useAppSelector(selectSelectedExportModeTab);

  const [isExportInProgress, setIsExportInProgress] = useState(false);

  const supportedPointCloudFormats = usePointCloudFormats();

  const { canUsePointCloudExport, canUseOrthoPhotoExport, canUseScansExport } =
    useExportModePermissions();

  const isScansExportSupported = useIsExportableDataset();

  useEnsureModeIsAllowed();

  return (
    <Drawer
      variant="persistent"
      open
      sx={{
        "& .MuiDrawer-paper": {
          position: "unset",
          width: 300,
          backgroundColor: grey[50],
          p: 1.5,
          gap: 3,

          "& .MuiTabs-list > .MuiButtonBase-root": {
            p: 1,
          },
        },
      }}
    >
      <FaroText variant="heading16">Export settings</FaroText>

      <TabContext value={selectedTab}>
        <Tabs
          value={selectedTab}
          onChange={(_, tab) => dispatch(setSelectedExportModeTab(tab))}
        >
          {canUsePointCloudExport &&
            shouldShowTab(ExportModeTab.pointCloud, hiddenTabs) && (
              <Tab
                label="Point cloud"
                value={ExportModeTab.pointCloud}
                aria-label="export point cloud tab"
                disabled={isExportInProgress}
              />
            )}
          {canUseOrthoPhotoExport &&
            shouldShowTab(ExportModeTab.orthophoto, hiddenTabs) && (
              <Tab
                label="Orthophoto"
                value={ExportModeTab.orthophoto}
                aria-label="export orthophoto tab"
                disabled={isExportInProgress}
              />
            )}
          {canUseScansExport &&
            isScansExportSupported &&
            shouldShowTab(ExportModeTab.scans, hiddenTabs) && (
              <Tab
                label="Scans"
                value={ExportModeTab.scans}
                aria-label="export scans tab"
                disabled={isExportInProgress}
              />
            )}
        </Tabs>
        {canUsePointCloudExport &&
          shouldShowTab(ExportModeTab.pointCloud, hiddenTabs) && (
            <TabPanel value={ExportModeTab.pointCloud} sx={TAB_PANEL_SX}>
              <PointCloudExportForm
                formats={supportedPointCloudFormats}
                isExportInProgress={isExportInProgress}
                onChangeIsExportInProgress={setIsExportInProgress}
              />
            </TabPanel>
          )}
        {canUseOrthoPhotoExport &&
          shouldShowTab(ExportModeTab.orthophoto, hiddenTabs) && (
            <TabPanel value={ExportModeTab.orthophoto} sx={TAB_PANEL_SX}>
              <OrthophotoExportForm />
            </TabPanel>
          )}
        {canUseScansExport &&
          isScansExportSupported &&
          shouldShowTab(ExportModeTab.scans, hiddenTabs) && (
            <TabPanel value={ExportModeTab.scans} sx={TAB_PANEL_SX}>
              <ScansExportForm />
            </TabPanel>
          )}
      </TabContext>
    </Drawer>
  );
}

/**
 * Ensures that the selected export mode is allowed.
 * Switches to a different allowed mode, if not.
 */
function useEnsureModeIsAllowed(): void {
  const dispatch = useAppDispatch();

  const selectedTab = useAppSelector(selectSelectedExportModeTab);

  const { canUsePointCloudExport, canUseOrthoPhotoExport, canUseScansExport } =
    useExportModePermissions();

  useLayoutEffect(() => {
    const allowedModes = [
      canUsePointCloudExport ? ExportModeTab.pointCloud : undefined,
      canUseOrthoPhotoExport ? ExportModeTab.orthophoto : undefined,
      canUseScansExport ? ExportModeTab.scans : undefined,
    ].filter(isValid);

    const firstValidMode = allowedModes.at(0);

    assert(firstValidMode, "Expected at least one mode to be available");

    if (!allowedModes.includes(selectedTab)) {
      dispatch(setSelectedExportModeTab(firstValidMode));
    }
  }, [
    canUseOrthoPhotoExport,
    canUsePointCloudExport,
    canUseScansExport,
    dispatch,
    selectedTab,
  ]);
}

function shouldShowTab(
  tab: ExportModeTab,
  hiddenTabs: ExportModeTab[],
): boolean {
  return !hiddenTabs.includes(tab);
}
